import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, FileText, ExternalLink, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { useScreenInit } from '../useScreenInit.js';
export function FacultyProfilePage() {
  const screenInit = useScreenInit();
  const [activeTab, setActiveTab] = useState(screenInit.activeTab ?? 'Research Graph');
  return <div className="bg-white min-h-screen pt-32 pb-20 relative" data-id="element-936">
      <div className="max-w-6xl mx-auto px-6" data-id="element-937">
        {/* Top Biographical Block */}
        <div className="bg-white border border-slate-200 shadow-lg rounded-3xl p-8 md:p-12 mb-12 relative overflow-hidden" data-id="element-938">
          <div className="flex flex-col md:flex-row gap-10 relative z-10" data-id="element-939">
            {/* Left: Portrait */}
            <div className="shrink-0" data-id="element-940">
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden border-2 border-gold p-1 bg-white relative group" data-id="element-941">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1976&auto=format&fit=crop" alt="Dr. Anika Rahman" className="w-full h-full object-cover rounded-xl" data-id="element-942" />
              </div>
            </div>

            {/* Right: Info */}
            <div className="flex-grow flex flex-col justify-center" data-id="element-943">
              <div className="inline-flex items-center space-x-2 bg-teal/10 border border-teal/20 rounded-full px-3 py-1 mb-4 w-fit" data-id="element-944">
                <span className="w-2 h-2 rounded-full bg-teal animate-pulse" data-id="element-945" />
                <span className="text-[10px] font-bold tracking-wider uppercase text-teal" data-id="element-946">
                  Accepting Grad Students
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2 text-navy" data-id="element-947">
                Dr. Anika Rahman
              </h1>
              <p className="text-xl text-slate-600 mb-8" data-id="element-948">
                Professor, Department of Computer Science & Engineering
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-949">
                <div className="space-y-3" data-id="element-950">
                  <div className="flex items-center text-sm text-slate-700" data-id="element-951">
                    <Mail className="w-4 h-4 mr-3 text-slate-400" data-id="element-952" />{' '}
                    anika.rahman@ewubd.edu
                  </div>
                  <div className="flex items-center text-sm text-slate-700" data-id="element-953">
                    <Phone className="w-4 h-4 mr-3 text-slate-400" data-id="element-954" />{' '}
                    +880-2-55046678 Ext: 305
                  </div>
                  <div className="flex items-center text-sm text-slate-700" data-id="element-955">
                    <MapPin className="w-4 h-4 mr-3 text-slate-400" data-id="element-956" /> Room 425,
                    Academic Building
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border-l-4 border-l-gold" data-id="element-957">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center" data-id="element-958">
                    <Clock className="w-3 h-3 mr-1.5" data-id="element-959" /> Consulting Hours
                  </div>
                  <div className="text-sm font-medium text-slate-800" data-id="element-960">
                    Sun & Tue: 11:00 AM - 1:00 PM
                  </div>
                  <div className="text-sm font-medium text-slate-800" data-id="element-961">
                    Wed: 2:00 PM - 4:00 PM
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-8 overflow-x-auto hide-scrollbar pb-2" data-id="element-962">
          {['Biography & Education', 'Research Graph', 'Publications & Journals'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-3 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${activeTab === tab ? 'bg-navy text-white shadow-md' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-navy'}`} data-id="element-963">
              {tab}
            </button>)}
        </div>

        {/* Tab Content */}
        <div className="min-h-[500px]" data-id="element-964">
          <AnimatePresence mode="wait" data-id="element-965">
            {activeTab === 'Biography & Education' && <motion.div key="bio" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-966">
                <BioTab data-id="element-967" />
              </motion.div>}
            {activeTab === 'Research Graph' && <motion.div key="research" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-968">
                <ResearchGraphTab data-id="element-969" />
              </motion.div>}
            {activeTab === 'Publications & Journals' && <motion.div key="pubs" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-970">
                <PublicationsTab data-id="element-971" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Floating Action Button */}
      <Link to="/faculty/cv-generator" className="fixed right-6 bottom-8 z-40 bg-gold hover:bg-gold-hover text-white font-bold py-3 px-6 rounded-full shadow-lg flex items-center transform hover:-translate-y-1 transition-all" data-id="element-972">
        <FileText className="w-5 h-5 mr-2" data-id="element-973" />
        <span className="hidden md:inline" data-id="element-974">Generate Official Academic CV</span>
        <span className="md:hidden" data-id="element-975">CV</span>
      </Link>
    </div>;
}
function BioTab() {
  const timeline = [{
    year: '2010',
    degree: 'Ph.D. in Computer Science',
    inst: 'Massachusetts Institute of Technology (MIT)',
    desc: 'Thesis: Advanced Neural Architectures for NLP'
  }, {
    year: '2006',
    degree: 'M.S. in Computer Science',
    inst: 'Stanford University',
    desc: 'Focus on Artificial Intelligence and Machine Learning'
  }, {
    year: '2003',
    degree: 'B.Sc. in Computer Science',
    inst: 'University of Dhaka',
    desc: 'Graduated with Highest Honors (Gold Medalist)'
  }];
  return <div className="bg-white border border-slate-200 shadow-md rounded-3xl p-8 md:p-10" data-id="element-976">
      <h3 className="text-2xl font-bold mb-8 text-navy" data-id="element-977">Academic Journey</h3>
      <div className="relative border-l-2 border-slate-200 ml-3 md:ml-4 space-y-10 py-2" data-id="element-978">
        {timeline.map((item, i) => <div key={i} className="relative pl-8 md:pl-10" data-id="element-979">
            <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-white border-2 border-gold" data-id="element-980" />
            <div className="text-gold font-bold mb-1" data-id="element-981">{item.year}</div>
            <h4 className="text-xl font-bold text-slate-900 mb-1" data-id="element-982">
              {item.degree}
            </h4>
            <div className="text-teal font-medium mb-2" data-id="element-983">{item.inst}</div>
            <p className="text-slate-600 text-sm" data-id="element-984">{item.desc}</p>
          </div>)}
      </div>
    </div>;
}
function ResearchGraphTab() {
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const nodes = [{
    id: 'ml',
    label: 'Machine Learning',
    x: 20,
    y: 20
  }, {
    id: 'nlp',
    label: 'NLP',
    x: 80,
    y: 30
  }, {
    id: 'bio',
    label: 'Bioinformatics',
    x: 25,
    y: 80
  }, {
    id: 'cv',
    label: 'Computer Vision',
    x: 75,
    y: 75
  }];
  const papers = {
    ml: [{
      title: 'Deep Reinforcement Learning in Resource Allocation',
      citations: 142,
      year: 2024
    }, {
      title: 'Federated Learning for Edge Devices',
      citations: 89,
      year: 2023
    }],
    nlp: [{
      title: 'Cross-lingual Transfer Learning for Bengali',
      citations: 215,
      year: 2025
    }, {
      title: 'Transformer Models for Low-Resource Languages',
      citations: 176,
      year: 2022
    }],
    bio: [{
      title: 'Genomic Sequence Analysis using CNNs',
      citations: 304,
      year: 2021
    }],
    cv: [{
      title: 'Real-time Object Detection in Medical Imaging',
      citations: 120,
      year: 2024
    }]
  };
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[500px]" data-id="element-985">
      {/* Interactive Graph Area */}
      <div className="lg:col-span-2 bg-white border border-slate-200 shadow-md rounded-3xl p-6 relative overflow-hidden flex items-center justify-center" data-id="element-986">
        <div className="absolute top-4 left-6 text-sm font-bold text-slate-400 uppercase tracking-widest" data-id="element-987">
          Interactive Node Map
        </div>

        <div className="relative w-full max-w-md aspect-square" data-id="element-988">
          {/* SVG Lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" data-id="element-989">
            {nodes.map(node => <line key={`line-${node.id}`} x1="50" y1="50" x2={node.x} y2={node.y} stroke={activeNode === node.id ? '#006B9A' : '#cbd5e1'} strokeWidth={activeNode === node.id ? '1.5' : '1'} className="transition-all duration-300" data-id="element-990" />)}
          </svg>

          {/* Center Node (Professor) */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" data-id="element-991">
            <div className="w-16 h-16 rounded-full bg-gold flex items-center justify-center shadow-md" data-id="element-992">
              <span className="text-white font-bold text-xs text-center leading-tight" data-id="element-993">
                Dr.
                <br data-id="element-994" />
                Rahman
              </span>
            </div>
          </div>

          {/* Radiating Nodes */}
          {nodes.map(node => <div key={node.id} className="absolute z-20 cursor-pointer transform -translate-x-1/2 -translate-y-1/2 transition-transform hover:scale-110" style={{
          top: `${node.y}%`,
          left: `${node.x}%`
        }} onMouseEnter={() => setActiveNode(node.id)} onMouseLeave={() => setActiveNode(null)} data-id="element-995">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center text-center p-2 border-2 transition-all duration-300 ${activeNode === node.id ? 'bg-teal/10 border-teal text-navy shadow-[0_0_20px_rgba(0,107,154,0.25)]' : 'bg-slate-50 border-slate-300 text-slate-600'}`} data-id="element-996">
                <span className="text-xs font-bold" data-id="element-997">{node.label}</span>
              </div>
            </div>)}
        </div>
      </div>

      {/* Info Panel */}
      <div className="bg-white border border-slate-200 shadow-md rounded-3xl p-6 flex flex-col" data-id="element-998">
        <h3 className="text-lg font-bold mb-4 border-b border-slate-200 pb-4 text-navy" data-id="element-999">
          {activeNode ? nodes.find(n => n.id === activeNode)?.label + ' Papers' : 'Hover a node to view papers'}
        </h3>

        <div className="flex-grow overflow-y-auto pr-2 space-y-4 custom-scrollbar" data-id="element-1000">
          {activeNode ? papers[activeNode as keyof typeof papers].map((paper, i) => <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: i * 0.1
        }} key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:border-teal transition-colors" data-id="element-1001">
                <h4 className="font-semibold text-sm mb-2 leading-snug text-slate-900" data-id="element-1002">
                  {paper.title}
                </h4>
                <div className="flex justify-between items-center text-xs text-slate-500" data-id="element-1003">
                  <span data-id="element-1004">{paper.year}</span>
                  <span className="text-teal font-medium" data-id="element-1005">
                    {paper.citations} Citations
                  </span>
                </div>
              </motion.div>) : <div className="h-full flex items-center justify-center text-slate-400 text-sm text-center px-4" data-id="element-1006">
              Explore the interactive graph to discover specific research
              pillars and associated publications.
            </div>}
        </div>
      </div>
    </div>;
}
function PublicationsTab() {
  const pubs = [{
    type: 'Journal Article',
    title: 'Cross-lingual Transfer Learning for Bengali Natural Language Processing',
    venue: 'IEEE Transactions on Pattern Analysis',
    year: '2025',
    links: ['Google Scholar', 'IEEE']
  }, {
    type: 'Conference Paper',
    title: 'Deep Reinforcement Learning in Resource Allocation for Edge Computing',
    venue: 'NeurIPS 2024',
    year: '2024',
    links: ['Scopus']
  }, {
    type: 'Journal Article',
    title: 'Genomic Sequence Analysis using Convolutional Neural Networks',
    venue: 'Bioinformatics Journal',
    year: '2021',
    links: ['Google Scholar', 'Scopus']
  }];
  return <div className="bg-white border border-slate-200 shadow-md rounded-3xl p-8" data-id="element-1007">
      <div className="flex justify-between items-center mb-8" data-id="element-1008">
        <h3 className="text-2xl font-bold text-navy" data-id="element-1009">Selected Publications</h3>
        <div className="text-sm text-slate-500" data-id="element-1010">
          Showing top 3 of 45+ publications
        </div>
      </div>

      <div className="space-y-4" data-id="element-1011">
        {pubs.map((pub, i) => <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:bg-white hover:shadow-sm transition-all group" data-id="element-1012">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-1013">
              <div data-id="element-1014">
                <div className="text-xs font-bold text-teal uppercase tracking-wider mb-2" data-id="element-1015">
                  {pub.type}
                </div>
                <h4 className="text-lg font-bold text-slate-900 mb-2 leading-snug" data-id="element-1016">
                  {pub.title}
                </h4>
                <div className="text-sm text-slate-500" data-id="element-1017">
                  {pub.venue} • {pub.year}
                </div>
              </div>
              <div className="flex space-x-2 shrink-0" data-id="element-1018">
                {pub.links.map(link => <button key={link} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-medium text-slate-600 hover:bg-navy hover:text-white hover:border-navy transition-colors flex items-center" data-id="element-1019">
                    {link}{' '}
                    <ExternalLink className="w-3 h-3 ml-1.5 opacity-50" data-id="element-1020" />
                  </button>)}
              </div>
            </div>
          </div>)}
      </div>

      <button className="w-full mt-6 py-4 rounded-xl border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-navy transition-colors" data-id="element-1021">
        View Full Publication History
      </button>
    </div>;
}